import { useEffect, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { toast } from '@qodo/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { GIT_PROVIDERS } from '../../consts/git';
import { useNavigateWithQueryParams } from '../../hooks/useNavigateWithQueryParams';
import { useUser } from '../../hooks/useUser';
import { useWizardAnalytics } from '../../hooks/useWizardAnalytics';
import { Github } from '../../logos';
import { QUERY_KEYS } from '../../network/queries/consts';
import { QUERY_PARAMS, ROUTES } from '../../routes/consts';
import {
  DialogTitleWithIcon,
  MultiStepDialog,
} from '../../shared/MultiStepDialog';
import { spawn } from '../../utils/translations';
import { Loader } from '../Loader';
import { getSteps } from './configuration';
import { MARKETPLACE_PHASES } from './consts';
import { GithubPublicAppInstallationContext } from './context';
import { useGithubMarketplaceFlow } from './hooks';
import { getValidatedInstallationErrorCode } from './utils';

export const GithubPublicAppInstallationDialog = () => {
  const errorHandledRef = useRef(false);

  const { hasGithubCloudWizardAccess, isGithubCloudWizardLoading } = useUser();
  const { navigateWithQueryParams } = useNavigateWithQueryParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { isMarketplaceFlow, marketplacePhase, marketplaceErrorCode } =
    useGithubMarketplaceFlow();

  const spawnGeneral = spawn('dialogs.github-mt-installation.general');
  const spawnErrors = spawn('dialogs.github-mt-installation.errors');

  const hasInstallationId = !!searchParams.get(QUERY_PARAMS.INSTALLATION_ID);
  const installationErrorCode = getValidatedInstallationErrorCode();
  const isErrorResultStep =
    !!installationErrorCode ||
    (isMarketplaceFlow && marketplacePhase === MARKETPLACE_PHASES.ERROR);

  const steps = getSteps({ isErrorResultStep });
  const {
    onStepViewed,
    onStepNavigated,
    onCancel: trackClose,
    onWizardCompleted,
  } = useWizardAnalytics({
    gitProvider: GIT_PROVIDERS.GITHUB,
    wizardVersion: 'v1',
    steps,
  });

  // This effect is for handling error codes that come through query params on initial load which can happen
  // when GitHub redirects back to the dialog after a failed installation attempt.
  useEffect(() => {
    if (isMarketplaceFlow) return;

    if (!installationErrorCode || errorHandledRef.current) return;

    toast.error(spawnErrors(installationErrorCode));
    errorHandledRef.current = true;
  }, [installationErrorCode, isMarketplaceFlow]);

  const close = () => {
    if (searchParams.has(QUERY_PARAMS.ERROR_CODE)) {
      const cleaned = new URLSearchParams(searchParams);

      cleaned.delete(QUERY_PARAMS.ERROR_CODE);
      setSearchParams(cleaned, { replace: true });
    }

    navigateWithQueryParams({ pathname: ROUTES.INTEGRATIONS });
  };

  const onCancel = (stepIndex: number, stepId: string) => {
    trackClose(stepIndex, stepId);
    close();
  };

  const onComplete = () => {
    onWizardCompleted();
    close();
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.INSTALLATIONS_COUNTS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_INSTALLATIONS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEYS.GIT_REPOSITORIES],
    });
  };

  if (isGithubCloudWizardLoading) {
    return <Loader center />;
  }

  if (!hasGithubCloudWizardAccess) {
    return <Navigate to={ROUTES.ROOT} />;
  }

  const resultStepIndex = 1;
  const shouldSkipToResultStep =
    hasInstallationId || !!installationErrorCode || isMarketplaceFlow;

  return (
    <GithubPublicAppInstallationContext.Provider
      value={{
        marketplacePhase,
        marketplaceErrorCode,
      }}
    >
      <MultiStepDialog
        title={
          <DialogTitleWithIcon text={spawnGeneral('title')}>
            <Github size={24} />
          </DialogTitleWithIcon>
        }
        steps={steps}
        onCancel={onCancel}
        onComplete={onComplete}
        onStepViewed={onStepViewed}
        onStepNavigated={onStepNavigated}
        className="sm:max-w-250!"
        disableOutsideClick={true}
        disableEscapeKey={true}
        defaultEarliestStepIndexAvailable={
          shouldSkipToResultStep ? resultStepIndex : 0
        }
        defaultFurthestStepIndex={shouldSkipToResultStep ? resultStepIndex : 0}
      />
    </GithubPublicAppInstallationContext.Provider>
  );
};
